import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Gift, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const DISMISS_KEY = "bb_exit_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 7;

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - parseInt(v, 10) < DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

// Only fire on marketing-style pages — not while user is in the middle of using a tool.
const ELIGIBLE_PATHS = ["/", "/pricing", "/how-it-works", "/gallery", "/about"];

export default function ExitIntent() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (recentlyDismissed()) return;
    if (!ELIGIBLE_PATHS.includes(location.pathname)) return;
    // Only desktop — mouseout doesn't work well on mobile
    if (typeof window === "undefined" || window.matchMedia("(max-width: 768px)").matches) return;

    let armed = false;
    // Arm after 8s — don't fire instantly on landing (annoying)
    const armTimer = setTimeout(() => { armed = true; }, 8000);

    const onLeave = (e) => {
      if (!armed) return;
      // Trigger when the cursor crosses the top of the viewport (typical "going for tab close" gesture)
      if (e.clientY <= 0 && !visible) {
        setVisible(true);
        trackEvent("exit_intent_shown", { path: location.pathname });
        document.removeEventListener("mouseleave", onLeave);
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [location.pathname, visible]);

  const dismiss = (reason) => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { console.debug("localStorage unavailable:", err?.name); }
    trackEvent("exit_intent_dismissed", { reason });
    setVisible(false);
  };

  const accepted = () => {
    trackEvent("exit_intent_accepted");
    trackEvent("free_design_cta", { location: "exit_intent" });
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { console.debug("localStorage unavailable:", err?.name); }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm animate-fade-up"
      role="dialog"
      data-testid="exit-intent-modal"
    >
      <div className="bg-[#F9F6F0] max-w-lg w-full rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-[#2D4A3E] text-[#F9F6F0] p-6 md:p-8 relative">
          <button
            onClick={() => dismiss("close_button")}
            className="absolute top-4 right-4 text-[#F9F6F0]/70 hover:text-[#F9F6F0]"
            aria-label="Close"
            data-testid="exit-intent-close"
          >
            <X className="w-5 h-5" />
          </button>
          <Gift className="w-10 h-10 text-[#D47C59] mb-4" strokeWidth={1.5} />
          <p className="overline text-[#D47C59] mb-2">Wait — before you go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight">
            Your first AI design is free
          </h2>
        </div>
        <div className="p-6 md:p-8">
          <p className="text-[#1A2F25] font-light leading-relaxed mb-2">
            Create a free account and get 1 AI design of your own backyard — plus the
            Material Calculator, Seasonal Planner, Buddy Care chats, and all 60+ of
            Linda's designs. No credit card. 20-second signup.
          </p>
          <p className="text-sm text-[#5C6B62] font-light mb-6 italic">
            "Most people who try it say it's the most fun they've had with their backyard
            since they bought their house." — Linda
          </p>
          <div className="flex gap-3">
            <Link
              to="/register"
              onClick={accepted}
              className="btn-accent flex-1 justify-center"
              data-testid="exit-intent-cta"
            >
              Create my free design
            </Link>
            <button
              onClick={() => dismiss("no_thanks")}
              className="btn-ghost text-sm text-[#5C6B62]"
              data-testid="exit-intent-no-thanks"
            >
              No thanks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
